import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Send, Loader2, X, MessageCircle, Sprout } from "lucide-react";
import { API } from "../context/AuthContext";
import { toast } from "sonner";

const GREETING = {
  en: "Hi! I'm your Hansa agronomist. Ask me anything about this scan — dosages, alternatives, or follow-up care.",
  hi: "नमस्ते! मैं आपका हंसा कृषि सलाहकार हूँ। इस स्कैन के बारे में कुछ भी पूछें — खुराक, विकल्प, या आगे की देखभाल।",
  mr: "नमस्कार! मी तुमचा हंसा कृषी सल्लागार आहे. या स्कॅनबद्दल काहीही विचारा — डोस, पर्याय किंवा पुढील काळजी.",
  ta: "வணக்கம்! நான் உங்கள் ஹன்சா வேளாண் ஆலோசகர். இந்த ஸ்கேன் பற்றி எதையும் கேளுங்கள் — அளவு, மாற்றுகள் அல்லது தொடர் பராமரிப்பு.",
  te: "నమస్తే! నేను మీ హంసా వ్యవసాయ సలహాదారు. ఈ స్కాన్ గురించి ఏదైనా అడగండి — మోతాదు, ప్రత్యామ్నాయాలు లేదా తదుపరి సంరక్షణ.",
};

const PLACEHOLDER = {
  en: "Ask about treatment, dosage, prevention...",
  hi: "उपचार, खुराक, रोकथाम के बारे में पूछें...",
  mr: "उपचार, डोस, प्रतिबंधाबद्दल विचारा...",
  ta: "சிகிச்சை, அளவு, தடுப்பு பற்றி கேளுங்கள்...",
  te: "చికిత్స, మోతాదు, నివారణ గురించి అడగండి...",
};

export const AgronomistChat = ({ open, onClose, scanId, language = "hi" }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([
        {
          role: "assistant",
          content: GREETING[language] || GREETING.en,
        },
      ]);
    }
  }, [open]); // eslint-disable-line

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || sending) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setSending(true);
    try {
      const r = await axios.post(`${API}/chat`, {
        scan_id: scanId || null,
        message: text,
        history: next.slice(0, -1),
        language,
      });
      setMessages([...next, { role: "assistant", content: r.data.reply }]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Chat failed");
      setMessages(next);
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="agronomist-chat-overlay"
      className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full sm:max-w-lg sm:rounded-3xl rounded-t-3xl shadow-2xl flex flex-col max-h-[85vh] overflow-hidden"
        data-testid="agronomist-chat-panel"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E0D8] bg-[#1B4332]">
          <div className="flex items-center gap-2 text-white">
            <Sprout className="w-5 h-5 text-[#D4A373]" />
            <span className="cd-heading font-bold">{(GREETING[language] && "हंसा कृषि सलाहकार") || "AI Agronomist"}</span>
          </div>
          <button
            onClick={onClose}
            data-testid="chat-close-btn"
            aria-label="Close chat"
            className="text-white/80 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div
          className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#FDFBF7]"
          data-testid="chat-messages"
        >
          {messages.map((m, i) => (
            <div
              key={i}
              data-testid={`chat-msg-${m.role}-${i}`}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-line ${
                  m.role === "user"
                    ? "bg-[#1B4332] text-white"
                    : "bg-white border border-[#E5E0D8] text-[#1C1C1C]"
                }`}
              >
                {m.content}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start" data-testid="chat-typing">
              <div className="bg-white border border-[#E5E0D8] rounded-2xl px-4 py-2.5 text-sm text-[#52796F] flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Thinking…
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <form
          onSubmit={send}
          className="border-t border-[#E5E0D8] p-3 flex gap-2 bg-white"
        >
          <input
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about treatment, dosage, prevention..."
            className="flex-1 bg-white border border-[#E5E0D8] rounded-full px-4 py-3 text-sm focus:outline-none focus:border-[#1B4332]"
          />
          <button
            type="submit"
            data-testid="chat-send-btn"
            disabled={sending || !input.trim()}
            className="bg-[#1B4332] text-white hover:bg-[#2D6A4F] rounded-full w-12 h-12 flex items-center justify-center disabled:opacity-50 transition-colors"
            aria-label="Send"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
};

export const ChatTrigger = ({ onClick, label = "Ask the agronomist" }) => (
  <button
    onClick={onClick}
    data-testid="chat-trigger-btn"
    className="bg-white text-[#1B4332] border-2 border-[#1B4332] hover:bg-[#F4F1EA] px-5 py-3 rounded-full font-bold flex items-center justify-center gap-2 cd-lift"
  >
    <MessageCircle className="w-4 h-4" />
    {label}
  </button>
);
